import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { EnquiryStorageNotice } from "@/components/EnquiryStorageNotice"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import type { PageProps } from "@/types/inertia"

type ViewingRequest = {
  id: number
  name: string
  email: string
  phone: string | null
  modality: string
  location_pref: string
  preferred_days: string[]
  notes: string | null
  created_at: string
}

type TherapyEnquiry = {
  id: number
  name: string
  email: string
  language_pref: string
  message: string
  location_pref: string
  open_to_online: boolean | null
  referral_source: string | null
  created_at: string
}

type Props = PageProps & {
  viewing_requests: ViewingRequest[]
  therapy_enquiries: TherapyEnquiry[]
  brand: "gisela" | "sanctuary" | null
  storing: boolean
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function EmptyRow({ colSpan, label }: { colSpan: number; label: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-8 text-center text-ink-muted">
        {label}
      </td>
    </tr>
  )
}

export default function EnquiriesIndex() {
  const { props } = usePage<Props>()
  const { viewing_requests, therapy_enquiries, brand, storing } = props
  const showViewing = brand !== "gisela"
  const showTherapy = brand !== "sanctuary"
  const pageTitle =
    brand === "gisela" ? "Therapy enquiries" : brand === "sanctuary" ? "Viewing requests" : "Enquiries"

  const [selectedViewing, setSelectedViewing] = React.useState<ViewingRequest | null>(null)
  const [selectedTherapy, setSelectedTherapy] = React.useState<TherapyEnquiry | null>(null)
  const [waitlistOnly, setWaitlistOnly] = React.useState(false)

  // "Waiting list" = asked for in person and did NOT tick "happy to start online".
  const visibleTherapyEnquiries = waitlistOnly
    ? therapy_enquiries.filter((e) => e.open_to_online === false)
    : therapy_enquiries

  return (
    <AdminShell>
      <Head title={pageTitle}>
        <meta name="description" content="View room viewing requests and therapy enquiries." />
        <meta property="og:title" content="Enquiries" />
        <meta property="og:description" content="View room viewing requests and therapy enquiries." />
      </Head>

      <PageHeader
        title={pageTitle}
        description={
          brand === "gisela"
            ? "Therapy enquiries submitted via Gisela's website."
            : brand === "sanctuary"
              ? "Room viewing requests submitted via the Sanctuary website."
              : "Viewing requests and therapy enquiries submitted via both websites."
        }
      />

      {!storing && <EnquiryStorageNotice what="Enquiries and viewing requests" />}

      {/* Viewing Requests */}
      {showViewing && (
      <section className="mt-8">
        <h2 className="mb-4">Room viewing requests</h2>
        <div className="overflow-hidden rounded-lg border border-hairline">
          <table className="w-full text-sm">
            <thead className="border-b border-hairline bg-surface">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-ink-muted">Name</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ink-muted sm:table-cell">Email</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ink-muted md:table-cell">Modality</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ink-muted lg:table-cell">Location</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ink-muted xl:table-cell">Days</th>
                <th className="px-4 py-3 text-left font-medium text-ink-muted">Received</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {viewing_requests.length === 0 && (
                <EmptyRow colSpan={6} label="No viewing requests yet." />
              )}
              {viewing_requests.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer bg-white transition-colors hover:bg-surface/30"
                  onClick={() => setSelectedViewing(r)}
                >
                  <td className="px-4 py-3 font-medium text-ink-display">{r.name}</td>
                  <td className="hidden px-4 py-3 text-ink-muted sm:table-cell">{r.email}</td>
                  <td className="hidden px-4 py-3 text-ink-body md:table-cell">{r.modality}</td>
                  <td className="hidden px-4 py-3 text-ink-body lg:table-cell">{r.location_pref}</td>
                  <td className="hidden px-4 py-3 text-ink-muted xl:table-cell">
                    {r.preferred_days.length > 0 ? r.preferred_days.join(", ") : "—"}
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{formatDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      )}

      {/* Therapy Enquiries */}
      {showTherapy && (
      <section className="mt-10">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2>Therapy enquiries</h2>
          <label className="flex items-center gap-2 font-normal text-ink-body">
            <Checkbox
              checked={waitlistOnly}
              onChange={(e) => setWaitlistOnly(e.target.checked)}
            />
            <span className="text-sm">In-person waiting list only</span>
          </label>
        </div>
        <div className="overflow-hidden rounded-lg border border-hairline">
          <table className="w-full text-sm">
            <thead className="border-b border-hairline bg-surface">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-ink-muted">Name</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ink-muted sm:table-cell">Email</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ink-muted md:table-cell">Language</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ink-muted lg:table-cell">Location</th>
                <th className="hidden px-4 py-3 text-left font-medium text-ink-muted xl:table-cell">Source</th>
                <th className="px-4 py-3 text-left font-medium text-ink-muted">Received</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {visibleTherapyEnquiries.length === 0 && (
                <EmptyRow
                  colSpan={6}
                  label={waitlistOnly ? "No one on the in-person waiting list." : "No therapy enquiries yet."}
                />
              )}
              {visibleTherapyEnquiries.map((e) => (
                <tr
                  key={e.id}
                  className="cursor-pointer bg-white transition-colors hover:bg-surface/30"
                  onClick={() => setSelectedTherapy(e)}
                >
                  <td className="px-4 py-3 font-medium text-ink-display">{e.name}</td>
                  <td className="hidden px-4 py-3 text-ink-muted sm:table-cell">{e.email}</td>
                  <td className="hidden px-4 py-3 text-ink-body md:table-cell">{e.language_pref}</td>
                  <td className="hidden px-4 py-3 text-ink-body lg:table-cell">
                    <span className="flex items-center gap-2">
                      {e.location_pref}
                      {e.open_to_online === false && <Badge tone="signal">Waiting list</Badge>}
                      {e.open_to_online === true && <Badge tone="accent">Open to online</Badge>}
                    </span>
                  </td>
                  <td className="hidden px-4 py-3 text-ink-muted xl:table-cell">
                    {e.referral_source ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{formatDate(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* Viewing Request Detail Dialog */}
      <Dialog open={selectedViewing !== null} onOpenChange={(open) => !open && setSelectedViewing(null)}>
        <DialogContent>
          {selectedViewing && (
            <>
              <DialogHeader>
                <DialogTitle>Viewing request — {selectedViewing.name}</DialogTitle>
              </DialogHeader>
              <dl className="mt-2 space-y-3 text-sm">
                <DetailRow label="Email" value={selectedViewing.email} />
                <DetailRow label="Phone" value={selectedViewing.phone ?? "Not provided"} />
                <DetailRow label="Modality" value={selectedViewing.modality} />
                <DetailRow label="Location preference" value={selectedViewing.location_pref} />
                <DetailRow
                  label="Preferred days"
                  value={selectedViewing.preferred_days.length > 0
                    ? selectedViewing.preferred_days.join(", ")
                    : "No preference"}
                />
                {selectedViewing.notes && (
                  <div>
                    <dt className="font-medium text-ink-display">Notes</dt>
                    <dd className="mt-1 text-ink-body whitespace-pre-wrap">{selectedViewing.notes}</dd>
                  </div>
                )}
                <DetailRow label="Received" value={formatDate(selectedViewing.created_at)} />
              </dl>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Therapy Enquiry Detail Dialog */}
      <Dialog open={selectedTherapy !== null} onOpenChange={(open) => !open && setSelectedTherapy(null)}>
        <DialogContent>
          {selectedTherapy && (
            <>
              <DialogHeader>
                <DialogTitle>Therapy enquiry — {selectedTherapy.name}</DialogTitle>
              </DialogHeader>
              <dl className="mt-2 space-y-3 text-sm">
                <DetailRow label="Email" value={selectedTherapy.email} />
                <DetailRow label="Language preference" value={selectedTherapy.language_pref} />
                <DetailRow label="Location preference" value={selectedTherapy.location_pref} />
                {selectedTherapy.open_to_online !== null && (
                  <DetailRow
                    label="Open to starting online"
                    value={selectedTherapy.open_to_online ? "Yes" : "No — waiting list"}
                  />
                )}
                <DetailRow label="Referral source" value={selectedTherapy.referral_source ?? "Not provided"} />
                <div>
                  <dt className="font-medium text-ink-display">Message</dt>
                  <dd className="mt-1 text-ink-body whitespace-pre-wrap">{selectedTherapy.message}</dd>
                </div>
                <DetailRow label="Received" value={formatDate(selectedTherapy.created_at)} />
              </dl>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 font-medium text-ink-display">{label}</dt>
      <dd className="text-ink-body">{value}</dd>
    </div>
  )
}
